/**
 * Client Portal Report Builder — RBAC-gated custom queries over Capital Assets.
 *
 * MVP scope:
 *   • Source: Capital Planning Assets (12,000+ Aroflo rows)
 *   • Guided UI: pick fields → build filters → optional group-by/aggregates
 *   • Natural-language shortcut: "Industrial machines at St Francis, cost > $20k"
 *     → Claude turns it into a structured definition (pre-fills the form).
 *   • Preview: on-screen table, paginated.
 *   • Exports: CSV / XLSX / PDF (branded).
 *   • Save: named reports, private per user.
 *
 * RBAC: enforced entirely server-side (see backend/report_builder.py). Client
 * roles never see other clients' data even if they craft a manual query.
 */
import { useEffect, useMemo, useState } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Sparkles, Filter, Plus, X, Play, Save, FileDown, FileSpreadsheet, FileText,
  Trash2, Loader2, Database, Info, BarChart3,
} from "lucide-react";
import { toast } from "sonner";

const fmtVal = (v) => {
  if (v === null || v === undefined) return "—";
  if (typeof v === "number") return new Intl.NumberFormat("en-AU").format(v);
  return String(v);
};

const OP_LABEL = {
  eq: "= equals",
  neq: "≠ not equals",
  in: "is one of",
  not_in: "is not one of",
  gt: "> greater than",
  gte: "≥ at least",
  lt: "< less than",
  lte: "≤ at most",
  between: "between",
  contains: "contains",
  starts: "starts with",
  empty: "is empty",
  not_empty: "is not empty",
};

const OPS_BY_DTYPE = {
  string: ["eq", "neq", "contains", "starts", "in", "not_in", "empty", "not_empty"],
  number: ["eq", "neq", "gt", "gte", "lt", "lte", "between", "empty", "not_empty"],
};

export default function ReportBuilderPage() {
  const [source, setSource] = useState(null);
  const [sources, setSources] = useState([]);
  const [fields, setFields] = useState([]);        // selected columns
  const [filters, setFilters] = useState([]);
  const [groupBy, setGroupBy] = useState([]);
  const [aggregates, setAggregates] = useState([]);
  const [sort, setSort] = useState([]);
  const [limit, setLimit] = useState(500);

  const [nlText, setNlText] = useState("");
  const [nlBusy, setNlBusy] = useState(false);

  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [totalMatching, setTotalMatching] = useState(null);
  const [running, setRunning] = useState(false);

  const [saved, setSaved] = useState([]);
  const [saveName, setSaveName] = useState("");
  const [saving, setSaving] = useState(false);

  // ---------------- initial load ----------------
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/reports/sources");
        setSources(data.sources);
        if (data.sources.length) setSource(data.sources[0]);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to load report sources");
      }
    })();
    loadSaved();
  }, []);

  const loadSaved = async () => {
    try {
      const { data } = await api.get("/reports/saved");
      setSaved(data.items || []);
    } catch (e) { console.warn("[ReportBuilder] loadSaved failed", e?.message || e); }
  };

  const fieldMap = useMemo(() => {
    const m = {};
    (source?.fields || []).forEach((f) => { m[f.key] = f; });
    return m;
  }, [source]);

  const dtypeOf = (key) => fieldMap[key]?.dtype || "string";

  // ---------------- Actions ----------------

  const toggleField = (key) => {
    setFields((prev) => prev.includes(key) ? prev.filter((f) => f !== key) : [...prev, key]);
  };

  const addFilter = () => {
    if (!source?.fields?.length) return;
    const f = source.fields[0];
    setFilters((prev) => [...prev, { field: f.key, op: "contains", value: "" }]);
  };
  const updateFilter = (i, patch) => {
    setFilters((prev) => prev.map((f, idx) => idx === i ? { ...f, ...patch } : f));
  };
  const removeFilter = (i) => setFilters((prev) => prev.filter((_, idx) => idx !== i));

  const cleanDefinition = () => {
    const def = {
      source: source.id,
      fields,
      filters: filters.map((f) => {
        // Coerce numbers where field dtype = number
        let v = f.value;
        if (dtypeOf(f.field) === "number" && f.op !== "empty" && f.op !== "not_empty") {
          if (f.op === "between") {
            v = (Array.isArray(v) ? v : String(v).split(",")).map(Number);
          } else if (f.op === "in" || f.op === "not_in") {
            v = String(v).split(",").map((s) => Number(s.trim())).filter((n) => !Number.isNaN(n));
          } else {
            v = Number(v);
          }
        } else if (f.op === "in" || f.op === "not_in") {
          v = String(v).split(",").map((s) => s.trim()).filter(Boolean);
        }
        return { field: f.field, op: f.op, value: v };
      }),
      group_by: groupBy,
      aggregates,
      sort,
      limit,
    };
    return def;
  };

  const run = async () => {
    if (!source) return;
    setRunning(true);
    try {
      const { data } = await api.post("/reports/run", cleanDefinition());
      setRows(data.rows || []);
      setRowCount(data.row_count || 0);
      setTotalMatching(data.total_matching);
      toast.success(`Loaded ${data.row_count} row${data.row_count === 1 ? "" : "s"}${data.total_matching ? ` of ${data.total_matching} matching` : ""}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Report failed");
    } finally { setRunning(false); }
  };

  const runNL = async () => {
    if (!nlText.trim()) return;
    setNlBusy(true);
    try {
      const { data } = await api.post("/reports/nl-to-def", { question: nlText, source: source?.id });
      const def = data.definition;
      setFields(def.fields || []);
      setFilters((def.filters || []).map((f) => ({
        ...f,
        value: Array.isArray(f.value) ? f.value.join(",") : f.value,
      })));
      setGroupBy(def.group_by || []);
      setAggregates(def.aggregates || []);
      setSort(def.sort || []);
      if (def.limit) setLimit(def.limit);
      toast.success("AI populated your report");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI parse failed");
    } finally { setNlBusy(false); }
  };

  const exportAs = async (format) => {
    try {
      const resp = await api.post("/reports/export",
        { format, title: saveName || "Report", definition: cleanDefinition() },
        { responseType: "blob" });
      const filename = /filename="([^"]+)"/.exec(resp.headers["content-disposition"] || "")?.[1] || `report.${format}`;
      const blob = new Blob([resp.data]);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed");
    }
  };

  const saveReport = async () => {
    if (!saveName.trim()) { toast.error("Give the report a name first"); return; }
    setSaving(true);
    try {
      await api.post("/reports/saved", { name: saveName.trim(), definition: cleanDefinition() });
      toast.success("Saved");
      setSaveName("");
      loadSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const loadSavedReport = (r) => {
    const def = r.definition;
    setFields(def.fields || []);
    setFilters((def.filters || []).map((f) => ({ ...f,
      value: Array.isArray(f.value) ? f.value.join(",") : f.value })));
    setGroupBy(def.group_by || []);
    setAggregates(def.aggregates || []);
    setSort(def.sort || []);
    if (def.limit) setLimit(def.limit);
    setSaveName(r.name);
    toast.info(`Loaded "${r.name}"`);
  };

  const deleteSavedReport = async (id) => {
    if (!confirm("Delete this saved report?")) return;
    try { await api.delete(`/reports/saved/${id}`); loadSaved(); }
    catch (e) { console.warn("[ReportBuilder] delete saved report failed", e?.message || e); }
  };

  // ---------------- Render ----------------

  const visibleColumns = fields.length ? fields : (source?.fields || []).map((f) => f.key);

  return (
    <AppLayout title="Report Builder" subtitle="Custom queries across GBE data · RBAC-scoped">
      <div className="grid grid-cols-12 gap-4">
        {/* Left column — data source, saved reports */}
        <div className="col-span-12 lg:col-span-3 space-y-4">
          <section className="rounded-xl border border-slate-200 bg-white p-4">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1.5 mb-2">
              <Database className="w-3.5 h-3.5" /> Data source
            </h3>
            <Select value={source?.id || ""} onValueChange={(v) => setSource(sources.find((s) => s.id === v))}>
              <SelectTrigger data-testid="report-source-select"><SelectValue placeholder="Pick a source…" /></SelectTrigger>
              <SelectContent>
                {sources.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {source && (
              <p className="mt-2 text-[11px] text-slate-500 leading-snug">{source.description}</p>
            )}
          </section>

          <section className="rounded-xl border border-slate-200 bg-white p-4">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
              My saved reports ({saved.length})
            </h3>
            {saved.length === 0 ? (
              <p className="text-[11px] text-slate-400 italic">Save a report to reuse it later.</p>
            ) : (
              <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
                {saved.map((r) => (
                  <div key={r.id} className="group flex items-center gap-1 rounded border border-slate-200 hover:border-gbe/50 hover:bg-slate-50 p-2 transition">
                    <button className="flex-1 text-left min-w-0"
                            data-testid={`report-load-${r.id}`}
                            onClick={() => loadSavedReport(r)}>
                      <div className="text-xs font-medium text-slate-800 truncate">{r.name}</div>
                      <div className="text-[10px] text-slate-500 truncate">
                        {r.definition.filters?.length || 0} filter{(r.definition.filters?.length || 0) === 1 ? "" : "s"} · {r.definition.fields?.length || (source?.fields.length || 0)} field{(r.definition.fields?.length || 1) === 1 ? "" : "s"}
                      </div>
                    </button>
                    <button className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-rose-600 transition"
                            data-testid={`report-delete-${r.id}`}
                            onClick={() => deleteSavedReport(r.id)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        {/* Middle column — builder */}
        <div className="col-span-12 lg:col-span-9 space-y-4">
          {/* Natural language shortcut */}
          <section className="rounded-xl border border-violet-200 bg-gradient-to-br from-violet-50/60 to-white p-4">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-violet-800 flex items-center gap-1.5 mb-2">
              <Sparkles className="w-3.5 h-3.5" /> Describe in plain English
            </h3>
            <div className="flex gap-2">
              <Textarea rows={2}
                        placeholder='e.g. "All industrial machines at St Francis Xavier with replacement cost over $20,000, sorted by cost descending"'
                        value={nlText}
                        onChange={(e) => setNlText(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) runNL(); }}
                        data-testid="report-nl-input"
                        className="flex-1 text-xs resize-none" />
              <Button onClick={runNL} disabled={nlBusy || !nlText.trim()}
                      data-testid="report-nl-generate"
                      className="bg-violet-600 hover:bg-violet-700 text-white self-stretch">
                {nlBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-1" />Generate</>}
              </Button>
            </div>
            <p className="text-[10px] text-violet-700/70 mt-1">Powered by Claude Sonnet · Populates the form below. You can then tweak & run.</p>
          </section>

          {/* Fields chooser */}
          <section className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Columns to show ({fields.length ? fields.length : "all"})
              </h3>
              <div className="flex gap-1.5">
                <Button size="sm" variant="ghost" className="h-7 text-[11px]"
                        onClick={() => setFields([])}
                        data-testid="report-fields-all">All</Button>
                <Button size="sm" variant="ghost" className="h-7 text-[11px]"
                        onClick={() => setFields((source?.fields || []).map((f) => f.key))}
                        data-testid="report-fields-clear">None</Button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1.5">
              {(source?.fields || []).map((f) => {
                const on = fields.includes(f.key);
                return (
                  <button key={f.key}
                          data-testid={`report-field-${f.key}`}
                          onClick={() => toggleField(f.key)}
                          className={`text-left px-2 py-1 rounded border text-[11px] transition ${
                            on ? "bg-gbe/10 border-gbe text-gbe-dark font-medium"
                               : "bg-white border-slate-200 hover:border-slate-400 text-slate-700"
                          }`}>
                    {f.label}
                    <span className="ml-1 text-[9px] text-slate-400 uppercase">{f.dtype}</span>
                  </button>
                );
              })}
            </div>
          </section>

          {/* Filters */}
          <section className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                <Filter className="w-3.5 h-3.5" /> Filters ({filters.length})
              </h3>
              <Button size="sm" variant="outline" onClick={addFilter} className="h-7 text-[11px]"
                      data-testid="report-add-filter">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add filter
              </Button>
            </div>
            {filters.length === 0 && (
              <p className="text-[11px] text-slate-400 italic">No filters — run will return all rows (up to {limit}).</p>
            )}
            <div className="space-y-2">
              {filters.map((f, i) => {
                const dtype = dtypeOf(f.field);
                const allowedOps = OPS_BY_DTYPE[dtype] || OPS_BY_DTYPE.string;
                return (
                  <div key={i} className="flex items-center gap-2 flex-wrap" data-testid={`report-filter-${i}`}>
                    <Select value={f.field} onValueChange={(v) => updateFilter(i, { field: v, op: OPS_BY_DTYPE[dtypeOf(v)][0] })}>
                      <SelectTrigger className="h-8 w-[180px] text-[11px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {(source?.fields || []).map((ff) => (
                          <SelectItem key={ff.key} value={ff.key}>{ff.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={f.op} onValueChange={(v) => updateFilter(i, { op: v })}>
                      <SelectTrigger className="h-8 w-[130px] text-[11px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {allowedOps.map((op) => <SelectItem key={op} value={op}>{OP_LABEL[op]}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {f.op !== "empty" && f.op !== "not_empty" && (
                      <Input value={f.value ?? ""}
                             onChange={(e) => updateFilter(i, { value: e.target.value })}
                             placeholder={f.op === "between" ? "min,max"
                                          : (f.op === "in" || f.op === "not_in") ? "a,b,c"
                                          : dtype === "number" ? "0" : "value"}
                             className="h-8 flex-1 min-w-[120px] text-[11px]" />
                    )}
                    <button onClick={() => removeFilter(i)}
                            className="text-slate-400 hover:text-rose-600"
                            data-testid={`report-remove-filter-${i}`}>
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Actions bar */}
          <section className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Button onClick={run} disabled={running || !source}
                        data-testid="report-run"
                        className="bg-gbe hover:bg-gbe-dark text-white">
                  {running ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Play className="w-4 h-4 mr-1" />}
                  Run report
                </Button>
                <div className="text-[11px] text-slate-500">
                  Limit&nbsp;
                  <Input type="number" value={limit}
                         onChange={(e) => setLimit(Math.max(1, Math.min(10000, Number(e.target.value))))}
                         className="inline-block w-20 h-7 text-[11px] px-2" />
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Input placeholder="Report name (to save)"
                       value={saveName}
                       onChange={(e) => setSaveName(e.target.value)}
                       data-testid="report-save-name"
                       className="h-8 w-52 text-[11px]" />
                <Button size="sm" variant="outline" onClick={saveReport}
                        disabled={saving || !saveName.trim()}
                        data-testid="report-save">
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                  Save
                </Button>
                <Button size="sm" variant="outline" onClick={() => exportAs("csv")} data-testid="report-export-csv" disabled={!rows.length}>
                  <FileText className="w-3.5 h-3.5 mr-1" /> CSV
                </Button>
                <Button size="sm" variant="outline" onClick={() => exportAs("xlsx")} data-testid="report-export-xlsx" disabled={!rows.length}>
                  <FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> XLSX
                </Button>
                <Button size="sm" variant="outline" onClick={() => exportAs("pdf")} data-testid="report-export-pdf" disabled={!rows.length}>
                  <FileDown className="w-3.5 h-3.5 mr-1" /> PDF
                </Button>
              </div>
            </div>
          </section>

          {/* Preview table */}
          <section className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                <BarChart3 className="w-3.5 h-3.5" /> Preview
              </h3>
              <div className="text-[11px] text-slate-500">
                {rowCount === 0 && !running ? "No rows yet — click Run"
                  : totalMatching !== null ? `${rowCount} of ${totalMatching} rows`
                  : `${rowCount} rows`}
              </div>
            </div>
            {rows.length === 0 ? (
              <div className="text-[11px] text-slate-400 text-center py-12 border border-dashed border-slate-200 rounded">
                {running ? "Loading…" : "Configure your report and click Run to preview."}
              </div>
            ) : (
              <div className="border border-slate-200 rounded overflow-x-auto max-h-[480px] overflow-y-auto">
                <table className="w-full text-xs" data-testid="report-preview-table">
                  <thead className="bg-slate-50 sticky top-0 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <tr>
                      {Object.keys(rows[0]).map((k) => (
                        <th key={k} className="text-left px-3 py-2 whitespace-nowrap">
                          {fieldMap[k]?.label || k}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i} className="border-b border-slate-100 hover:bg-slate-50/50">
                        {Object.keys(rows[0]).map((k) => (
                          <td key={k} className={`px-3 py-1.5 ${typeof r[k] === "number" ? "text-right tabular-nums" : ""}`}>
                            {fmtVal(r[k])}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="mt-2 text-[10px] text-slate-400 flex items-center gap-1">
              <Info className="w-3 h-3" />
              Client-role users automatically see only their own data. Audit trail persists every run.
            </p>
          </section>
        </div>
      </div>
    </AppLayout>
  );
}
